import { ITreeItem } from "@/interfaces";
import { MAX_TREE_LEVELS } from "@/constants";
import styles from "./PtTreeItem.module.scss";
import {
  PT_TREE_ITEM_TOGGLE_BUTTON_TESTID,
  getPtTreeItemTestId,
  getPtTreeItemButtonTestId,
} from "@/constants/test-ids";
import { BareButton, Loading } from "@/components";
import { MuxToggle } from "@/Icons";
import { useState } from "react";

interface PtTreeItemProps {
  data: ITreeItem;
  openNodes: Array<string>;
  selectedItem: ITreeItem | undefined;
  isRoot?: boolean;
  isLoading?: boolean;
  level?: number | string;
  error?: string | null;
  handleSelect: (node: ITreeItem) => void;
  handleToggleExpandState: (node: ITreeItem) => void;
}

export const PtTreeItem = ({
  data,
  openNodes,
  selectedItem,
  level = 0,
  isRoot,
  error = null,
  handleSelect,
  handleToggleExpandState,
}: PtTreeItemProps) => {
  const [loading, setLoading] = useState<boolean>(false);

  const isExpand = openNodes.includes(data.id);
  const depth =
    typeof level === "string" && level.includes("-")
      ? level.split("-").length
      : 0;
  const parentLevels = data?.serverObject?.ancestry
    ? data.serverObject.ancestry.split("/").length
    : 0;
  const effectiveMaxLevels = MAX_TREE_LEVELS + parentLevels;
  const isOtherNode = data.id.endsWith("/other_filter");

  const hasChildren =
    depth < effectiveMaxLevels &&
    !isOtherNode &&
    (data?.extra.hasChildren || data.children.length > 0);

  const isSelected = data.id == selectedItem?.id;
  const icon = data?.extra?.getIcon && data?.extra?.getIcon(isSelected);

  const getPadding = () => {
    // Case 1: Node has no children - apply standard padding
    if (!hasChildren) {
      return 17;
    }

    if (hasChildren || icon || (isRoot && !icon)) {
      return 0;
    }

    // Default case: Use standard padding for all other scenarios
    return 17;
  };

  const onToggleClick = async (node: ITreeItem) => {
    if (node.children.length === 0 && !isExpand) {
      setLoading(true);
      await handleToggleExpandState(node);
      setLoading(false);
    } else {
      await handleToggleExpandState(node);
    }
  };

  return (
    <li
      data-testid={getPtTreeItemTestId(level)}
      className={`${styles.ptTreeItem} ${data.extra.disabled ? styles.disabled : ""}`}
    >
      <div
        className={`${styles.node} ${styles.text} ${isSelected ? styles.selected : ""}`}
      >
        {hasChildren ? (
          <BareButton
            handleClick={(e) => {
              e.preventDefault();
              onToggleClick(data);
            }}
            dataTestId={PT_TREE_ITEM_TOGGLE_BUTTON_TESTID}
            className={`${isExpand ? styles["collapse-node"] : styles["expand-node"]}`}
          >
            <MuxToggle />
          </BareButton>
        ) : null}
        <div
          role="button"
          data-testid={getPtTreeItemButtonTestId(level)}
          onKeyDown={() => {}}
          tabIndex={0}
          className={styles.textContainer}
          style={{
            paddingLeft: getPadding(),
          }}
          onClick={() => !data.extra.disabled && handleSelect(data)}
        >
          {icon && <span className={styles.icon}>{icon}</span>}
          <p className={styles.ellipsisSingleLine}>{data.name}</p>
        </div>
      </div>

      {isExpand && hasChildren ? (
        <ul className={styles[`children-${loading ? "loading" : "ready"}`]}>
          {loading ? (
            <Loading size="modal" />
          ) : error ? (
            <li>{error}</li>
          ) : (
            data.children?.map((item: ITreeItem, index) => (
              <PtTreeItem
                isLoading={item.extra.isLoading}
                key={item.id}
                level={`${level || "0"}-${index}`}
                data={item}
                error={item.extra.error ?? null}
                selectedItem={selectedItem}
                handleSelect={handleSelect}
                handleToggleExpandState={handleToggleExpandState}
                openNodes={openNodes}
              />
            ))
          )}
        </ul>
      ) : null}
    </li>
  );
};
